import { useEffect, useState } from 'react'
import { Badge } from '../catalyst/badge'
import { Button } from '../catalyst/button'
import { Heading } from '../catalyst/heading'
import { Input, InputGroup } from '../catalyst/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../catalyst/table'
import { Text } from '../catalyst/text'
import { useSearchParams } from 'react-router-dom'
import { Link } from '../catalyst/link'
import { usePesticides } from '../lib/pesticides'
import { usePestProducts } from '../lib/scouting'
import { Shell } from './shell'
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid'

const DEBOUNCE_MS = 300

/**
 * Lookup by use case (`?pest=`, linked from a scouting entry): the current products registered for
 * blueberries against that pest. A list of registered options, not a recommendation (PRODUCT.md §9).
 */
function PestResults({ pest, label }: { pest: string; label: string }) {
  const { items, loading, error } = usePestProducts(pest)
  return (
    <Shell>
      <Link href="/pesticides" className="text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        All products
      </Link>
      <Heading className="mt-4">Registered for {label} on blueberry</Heading>
      <Text className="mt-2 max-w-2xl">
        Current products whose registration or checked label names {label}. These are registered options, not a recommendation: read the label for
        rates, timing and restrictions, and ask your agronomist which fits your situation.
      </Text>
      <div className="mt-8">
        {error ? (
          <Text>{error}</Text>
        ) : loading ? (
          <Text>Searching the registry…</Text>
        ) : items.length === 0 ? (
          <Text>No current blueberry product in the registry names {label}. Search by product name instead, or ask your agronomist.</Text>
        ) : (
          <Table striped>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeader>Active ingredient</TableHeader>
                <TableHeader>Type</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} href={`/pesticides/${p.id}`}>
                  <TableCell className="font-medium text-zinc-950 dark:text-white">{p.productNameEn}</TableCell>
                  <TableCell>{p.activeIngredientsRawEn || '—'}</TableCell>
                  <TableCell>{p.productType || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </Shell>
  )
}

export function PesticidesPage() {
  const [search] = useSearchParams()
  const pest = search.get('pest')
  if (pest) return <PestResults pest={pest} label={search.get('for') || pest} />
  return <PesticideSearch />
}

function PesticideSearch() {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  const { items, loading, loadingMore, error, hasMore, loadMore } = usePesticides(debouncedQuery)

  return (
    <Shell>
      <div className="flex items-center justify-between gap-4">
        <div>
          <Heading>Pesticides</Heading>
          <Text>Look up a registered product by name or active ingredient.</Text>
        </div>
      </div>

      <div className="mt-6 max-w-md">
        <InputGroup>
          <MagnifyingGlassIcon data-slot="icon" />
          <Input
            type="search"
            placeholder="Search by name or active ingredient…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search pesticides"
          />
        </InputGroup>
      </div>

      <div className="mt-8">
        {error ? (
          <div className="rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
            <Text>{error}</Text>
          </div>
        ) : loading ? (
          <div className="rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>Loading pesticides…</Text>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>{debouncedQuery ? `No products match "${debouncedQuery}".` : 'No products found.'}</Text>
          </div>
        ) : (
          <>
            <Table striped>
              <TableHead>
                <TableRow>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Active ingredient</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Status</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((pesticide) => (
                  <TableRow key={pesticide.id} href={`/pesticides/${pesticide.id}`}>
                    <TableCell className="font-medium text-zinc-950 dark:text-white">{pesticide.productNameEn}</TableCell>
                    <TableCell>{pesticide.activeIngredientsRawEn || '—'}</TableCell>
                    <TableCell>{pesticide.productType || '—'}</TableCell>
                    <TableCell>
                      <Badge color={pesticide.currentOrHistorical === 'Current' ? 'green' : 'zinc'}>{pesticide.currentOrHistorical}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {hasMore && !debouncedQuery && (
              <div className="mt-4 flex justify-center">
                <Button outline disabled={loadingMore} onClick={loadMore}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  )
}
